import { create } from "zustand";
import { makeRequest } from "../utils/makeRequest";
import { getObjectFromLocalStore } from "../utils/localStore";

export type PermissionLevel = "NONE" | "VIEW" | "EDIT";

export interface PermissionNode {
  label: string;
  levels: PermissionLevel[];
  level: PermissionLevel;
}

export type PermissionTree = {
  [key: string]: PermissionNode | PermissionTree;
};

function isPermissionNode(node: PermissionNode | PermissionTree): node is PermissionNode {
  return typeof (node as PermissionNode)?.level === "string";
}

// Single source of truth for what "locked"/"editable" mean for a permission level, so a future
// change to the level values only needs to change here rather than at every call site.
export function isPermissionLocked(level: PermissionLevel): boolean {
  return level === "NONE";
}

export function canEditPermission(level: PermissionLevel): boolean {
  return level === "EDIT";
}

// `path` is dot-notation into the tree returned by GET /assistant-access/{id}/permissions.
// A leaf path (e.g. "DASHBOARD.ANALYTICS") resolves to a PermissionNode (has .level/.label/
// .levels); a category path (e.g. "DASHBOARD") resolves to the PermissionTree of its children.
// Returns null if the path doesn't exist in the tree.
export function getPermissionNode(
  tree: PermissionTree | null,
  path: string,
): PermissionNode | PermissionTree | null {
  const node = path.split(".").reduce<PermissionNode | PermissionTree | null>((current, key) => {
    if (!current || isPermissionNode(current)) return null;
    return current[key] ?? null;
  }, tree);
  return node ?? null;
}

interface AssistantPermissionsState {
  permissions: PermissionTree | null;
  // True once permissions have been fetched for an assistant acting on behalf of an advisor.
  // Only while this is true does the permission catalogue apply at all - a user who isn't
  // acting as an assistant (an advisor/admin viewing their own data) isn't restricted by it.
  isAssistantContext: boolean;
  isLoading: boolean;
  // Set when the last fetch failed; `permissions` is cleared alongside it so stale data from a
  // previous advisor never lingers under a failed fetch for the new one.
  error: string | null;
  fetchPermissions: (userAssistantId: string) => Promise<void>;
  ensurePermissions: () => void;
  clearPermissions: () => void;
}

function selectedUserAssistantId(): string | null {
  const userAccess = getObjectFromLocalStore("useraccess");
  const assistantAgent = getObjectFromLocalStore("assistantAgentUserAccess");
  if (!userAccess?.isAssistant || !assistantAgent?.selectedAssistantAgentOption) return null;
  return assistantAgent?.userAssistantId ?? null;
}

export function isAssistantSession(): boolean {
  return selectedUserAssistantId() !== null;
}

// `userAssistantId` is the UserAssistant link record id for the current assistant + selected-
// advisor pairing (assistantAgents[].userAssistantId) - distinct from assistantAgents[].id,
// which is the advisor's own user id and is used as the `/auth/switch/{key}` key instead.
export const useAssistantPermissionsStore = create<AssistantPermissionsState>((set, get) => ({
  permissions: null,
  isAssistantContext: false,
  isLoading: false,
  error: null,
  fetchPermissions: async (userAssistantId) => {
    if (!userAssistantId) return;
    set({ isLoading: true, isAssistantContext: true, error: null });
    try {
      const response = await makeRequest("assistantAccessActivity").get(
        `${userAssistantId}/permissions`,
        true,
      );
      set({ permissions: response?.data?.data ?? null, isLoading: false });
    } catch (error: any) {
      set({
        permissions: null,
        isLoading: false,
        error: error?.message || "Failed to fetch permissions",
      });
    }
  },
  ensurePermissions: () => {
    const { permissions, isLoading, error, fetchPermissions } = get();
    if (permissions || isLoading || error) return;
    const userAssistantId = selectedUserAssistantId();
    if (!userAssistantId) return;
    fetchPermissions(userAssistantId);
  },
  clearPermissions: () =>
    set({ permissions: null, isAssistantContext: false, isLoading: false, error: null }),
}));
